import { isFulfilled, isRejected } from "@reduxjs/toolkit"

import { BASE_URL } from "@/shared/config/constants"
import type { StartParams } from "@/shared/router"
import { WebApp } from "@/shared/telegram"

import { classifyEntry } from "./entry-kind"
import { knownOnboardingButton, knownStartTab, knownUrlTab } from "./known-ids"
import { routeTemplateFromMatches } from "./route-template"
import { createUsageTracker, type UsageBatch, type UsageChatType, type UsageOutcome } from "./tracker"

import type { Middleware } from "@reduxjs/toolkit"

const USAGE_EVENTS_URL = `${BASE_URL}/api/v1/usage/events`
const PLATFORM_PATTERN = /^[a-z_]{1,20}$/
const CHAT_TYPES: readonly UsageChatType[] = ["private", "group", "supergroup", "channel", "sender"]
const INVOICE_OUTCOMES: readonly UsageOutcome[] = ["paid", "cancelled", "failed", "pending"]
// Onboarding reporters are analytics themselves; counting them as user saves would double-count.
const UNTRACKED_MUTATIONS: ReadonlySet<string> = new Set(["reportFirstContactEvent", "reportShowcaseEvent"])

const sendUsageBatch = async (batch: UsageBatch, keepalive: boolean): Promise<void> => {
  try {
    await fetch(USAGE_EVENTS_URL, {
      method: "POST",
      keepalive,
      headers: {
        "Content-Type": "application/json",
        "X-Telegram-Init-Data": WebApp.initData,
        Authorization: `Bearer ${WebApp.initData}`
      },
      body: JSON.stringify(batch)
    })
  } catch {
    // At-most-once delivery: a lost batch only lowers counts.
  }
}

// The server requires a UUID v4; randomUUID is missing outside secure contexts such as plain-HTTP dev hosts.
const newSessionId = (): string => {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID()
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("")
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

const usageTracker = createUsageTracker({
  now: Date.now,
  isEnabled: () => Boolean(WebApp.initData),
  newSessionId,
  platform: () => (PLATFORM_PATTERN.test(WebApp.platform) ? WebApp.platform : "unknown"),
  send: sendUsageBatch
})

const onVisibilityChange = () => {
  if (document.visibilityState === "hidden") usageTracker.hide()
  else usageTracker.show()
}

const onInvoiceClosed = ({ status }: { status: string }) => {
  const outcome = INVOICE_OUTCOMES.find(candidate => candidate === status)
  if (outcome) usageTracker.trackInvoiceClosed(outcome)
}

let listening = false

export const startUsageTracking = (params: StartParams, chatId: number | null) => {
  if (listening) return
  listening = true

  // Initialization can finish after the app was already hidden; that time is not foreground time.
  if (document.visibilityState === "hidden") usageTracker.hide()

  const launchChatType = WebApp.initDataUnsafe.chat_type
  usageTracker.start({
    entry: classifyEntry(params),
    name: params.onboardingButton === null ? undefined : knownOnboardingButton(params.onboardingButton),
    tab: params.tabParam === null ? undefined : knownStartTab(params.tabParam),
    chatType: CHAT_TYPES.find(chatType => chatType === launchChatType),
    chatId,
    adSource: params.adSource ?? undefined
  })

  document.addEventListener("visibilitychange", onVisibilityChange)
  window.addEventListener("pagehide", usageTracker.hide)
  WebApp.onEvent("activated", usageTracker.show)
  WebApp.onEvent("deactivated", usageTracker.hide)
  WebApp.onEvent("invoiceClosed", onInvoiceClosed)
}

export const setUsageChatId = usageTracker.setChatId

export const trackUsageTab = usageTracker.trackTab

interface ObservedRouterState {
  location: { search: string }
  matches: readonly { route: { path?: string } }[]
}

interface ObservedRouter {
  state: ObservedRouterState
  subscribe: (listener: (state: ObservedRouterState) => void) => () => void
}

export const observeUsageRoutes = (router: ObservedRouter) => {
  const record = (state: ObservedRouterState) => {
    usageTracker.setLocation(
      routeTemplateFromMatches(state.matches),
      knownUrlTab(new URLSearchParams(state.location.search).get("tab"))
    )
  }
  record(router.state)
  return router.subscribe(record)
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null

const rejectedOutcome = (payload: unknown): UsageOutcome => {
  if (!isRecord(payload)) return "network_error"
  const status = payload.status === "PARSING_ERROR" ? payload.originalStatus : payload.status
  if (typeof status === "number") return status >= 500 ? "server_error" : "client_error"
  if (status === "FETCH_ERROR" || status === "TIMEOUT_ERROR") return "network_error"
  return "client_error"
}

export const usageAnalyticsMiddleware: Middleware = () => next => action => {
  const result = next(action)
  if (!isFulfilled(action) && !isRejected(action)) return result

  const arg: unknown = action.meta.arg
  if (!isRecord(arg) || arg.type !== "mutation" || typeof arg.endpointName !== "string") return result
  if (UNTRACKED_MUTATIONS.has(arg.endpointName)) return result

  usageTracker.trackMutation(arg.endpointName, isFulfilled(action) ? "ok" : rejectedOutcome(action.payload))
  return result
}
